import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Button, Grid, InputAdornment, TablePagination, TextField } from '@mui/material';
import './Taxes.css'
import SearchIcon from "@mui/icons-material/Search";
import { useThemeContext } from '../../Context/ThemeContext.js';
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from '../../Utils/constants.js';
import ArrowButton from '../../Components/Common/ArrowButton.js';
import AccessTimeSharpIcon from '@mui/icons-material/AccessTimeSharp';
import { getRecentTaxReport, getTaxReport } from '../../Services/authService.js';
import TransactionsTable from '../Views/Transcations/TransactionsTable.js';
import TaxModal from './TaxModal.js';

function Taxes() {
  const { mode } = useThemeContext();
  const { t, i18n } = useTranslation(); // <-- get i18n
  const containerBackground = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const textColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
  const cardtitlesdivs = mode === 'light' ? LIGHTCOLORS.GreyScale10 : DARKCOLORS.GreyScale10;
  const clockColor = mode === 'dark' ? DARKCOLORS.Alert500 : LIGHTCOLORS.Alert500;
  const borderColor1 = mode === 'dark' ? DARKCOLORS.GreyScale50 : LIGHTCOLORS.GreyScale50;

  const columns = [
    { field: "month", label: t("taxes.month") },
    { field: "status", label: t("taxes.status") },
    { field: "filedDate", label: t("taxes.filedDate") },
    { field: "grossAmount", label: t("taxes.grossAmount") },
    { field: "netAmount", label: t("taxes.netAmount") },
    { field: "taxableAmount", label: t("taxes.taxableAmount") },
    { field: "dueAmount", label: t("taxes.dueAmount") },
  ];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // State for tax data, loading and error handling
  const [taxData, setTaxData] = useState(null);
  const [error, setError] = useState(null);

  const fetchTaxes = async () => {
    try {
      const data = await getTaxReport();
      setRows(data);
    } catch (err) {
      setError(t('taxes.Failedloaddata'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchRecentTaxes = async () => {
      try {
        const data = await getRecentTaxReport();

        // Map the API data to the format used by cards
        const mappedData = data.map((item) => ({
          month: item.month,
          title: `${new Date(item.month).toLocaleString("default", { month: "long" })}  ${t('taxes.MonthReport')}`,
          subtitle: item.status === "Pending" ? t('taxes.Pending') : t('taxes.Filed'),
          status: item.status,
          grossAmount: `€ ${item.grossAmount.toFixed(2)}`,
          netAmount: `€ ${item.netAmount.toFixed(2)}`,
          taxableAmount: `€ ${item.taxableAmount.toFixed(2)}`,
        }));

        setTaxData(mappedData);
      } catch (err) {
        setError(t("taxes.Failedloaddata"));
      } finally {
        setLoading(false);
      }
    };
    fetchRecentTaxes();
  }, [i18n.language]);

  const TaxModalSvg = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M5.8 3.4C5.8 2.76348 5.54714 2.15303 5.09706 1.70294C4.64697 1.25286 4.03652 1 3.4 1C2.76348 1 2.15303 1.25286 1.70294 1.70294C1.25286 2.15303 1 2.76348 1 3.4C1 4.03652 1.25286 4.64697 1.70294 5.09706C2.15303 5.54714 2.76348 5.8 3.4 5.8C4.03652 5.8 4.64697 5.54714 5.09706 5.09706C5.54714 4.64697 5.8 4.03652 5.8 3.4ZM5.8 10.6C5.8 9.96348 5.54714 9.35303 5.09706 8.90294C4.64697 8.45286 4.03652 8.2 3.4 8.2C2.76348 8.2 2.15303 8.45286 1.70294 8.90294C1.25286 9.35303 1 9.96348 1 10.6C1 11.2365 1.25286 11.847 1.70294 12.2971C2.15303 12.7471 2.76348 13 3.4 13C4.03652 13 4.64697 12.7471 5.09706 12.2971C5.54714 11.847 5.8 11.2365 5.8 10.6ZM13 3.4C13 2.76348 12.7471 2.15303 12.2971 1.70294C11.847 1.25286 11.2365 1 10.6 1C9.96348 1 9.35303 1.25286 8.90294 1.70294C8.45286 2.15303 8.2 2.76348 8.2 3.4C8.2 4.03652 8.45286 4.64697 8.90294 5.09706C9.35303 5.54714 9.96348 5.8 10.6 5.8C11.2365 5.8 11.847 5.54714 12.2971 5.09706C12.7471 4.64697 13 4.03652 13 3.4ZM13 10.6C13 9.96348 12.7471 9.35303 12.2971 8.90294C11.847 8.45286 11.2365 8.2 10.6 8.2C9.96348 8.2 9.35303 8.45286 8.90294 8.90294C8.45286 9.35303 8.2 9.96348 8.2 10.6C8.2 11.2365 8.45286 11.847 8.90294 12.2971C9.35303 12.7471 9.96348 13 10.6 13C11.2365 13 11.847 12.7471 12.2971 12.2971C12.7471 11.847 13 11.2365 13 10.6Z"
        stroke={textColor} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  useEffect(() => {
    fetchTaxes();
  }, [page, rowsPerPage, searchTerm]);

  return (
    <div className='dashboard-container-overview'>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 className='membership-text-heading' style={{ flexGrow: 1, alignSelf: 'normal', padding: 0 }}>
          {t('taxes.taxes')}
        </h2>
        <Button
          style={{
            background: 'transparent',
            color: textColor,
            border: `1px solid ${borderColor1}`,
            padding: "9.5px 26px",
            textTransform: "none",
            borderRadius: "24px"
          }}
          id='taxes-btn'
          className='add-machine '
          onClick={() => { setIsModalOpen(true) }}
          endIcon={<TaxModalSvg />}
        >
          {t('taxes.TaxDetails')}
        </Button>
      </Box>


      <Grid container spacing={3} sx={{ marginTop: "1px", mb: 6 }}>
        {taxData?.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", justifyContent: "center", }}>
            <Card className='taxes-cards' sx={{ width: "100%", maxWidth: "100%", p: 1, borderRadius: "12px", backgroundColor: containerBackground, boxShadow: "none" }}>
              <CardContent>
                <Grid container>
                  <Grid item xs={12} >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", }}>
                      <Box>
                        <p className='machine-name-text'>{card.title}</p>
                        {/* <Box sx={{ display: "flex", gap: "5px", alignItems: "center", mt: 1 }}>
                          {card.status === "Pending" && (
                            <AccessTimeSharpIcon style={{ color: clockColor, fontSize: "16px", marginTop: "2px" }} />
                          )}
                          <p className='add-machine' id='machineStreet' style={{ color: textColor, margin: "0px" }}>{card.subtitle}</p>
                        </Box> */}
                      </Box>
                      <ArrowButton targetRoute={ROUTES.TAXREPORT} franchise={card.month} />
                    </Box>
                    <Box sx={{ mt: 3 }}>
                      {/* Status */}
                      {/* <Box
                        sx={{
                          borderRadius: "8px",
                          padding: "15px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.5rem",
                          border: `1px solid ${cardtitlesdivs}`,
                        }}
                      >
                        <p className='label-text-sec' style={{ margin: "0px" }}>{t('taxes.status')}</p>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", width: "25%" }}>
                          <p className='add-machine' style={{ color: textColor, margin: "0px" }}>{card.status}</p>
                        </Box>
                      </Box> */}

                      {/* Gross Amount */}
                      <Box
                        sx={{
                          backgroundColor: cardtitlesdivs,
                          border: `1px solid ${cardtitlesdivs}`,
                          borderRadius: "8px",
                          padding: "15px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <p className='label-text-sec' style={{ margin: "0px" }}>{t('taxes.grossAmount')}</p>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", width: "25%" }}>
                          <p className='add-machine' style={{ color: textColor, margin: "0px" }}>{card.grossAmount}</p>
                        </Box>
                      </Box>

                      {/* Net Amount */}
                      <Box
                        sx={{
                          borderRadius: "8px",
                          padding: "15px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.5rem",
                          border: `1px solid ${cardtitlesdivs}`,
                        }}
                      >
                        <p className='label-text-sec' style={{ margin: "0px" }}>{t('taxes.netAmount')}</p>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", width: "25%" }}>
                          <p className='add-machine' style={{ color: textColor, margin: "0px", }}>{card.netAmount}</p>
                        </Box>
                      </Box>

                      {/* Taxable Amount */}
                      <Box
                        sx={{
                          backgroundColor: cardtitlesdivs,
                          border: `1px solid ${cardtitlesdivs}`,
                          borderRadius: "8px",
                          padding: "15px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <p className='label-text-sec' style={{ margin: "0px" }}>{t('taxes.taxableAmount')}</p>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start", width: "25%" }}>
                          <p className='add-machine' style={{ color: textColor, margin: "0px" }}>{card.taxableAmount}</p>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <h2 className='membership-text-heading'>{t('taxes.previousRecords')}</h2>
      <Box style={{ background: containerBackground, borderRadius: "12px", marginTop: "1.5rem" }}>
        <TextField
          placeholder={t("membershipCards.search")}
          value={searchTerm}
          onChange={handleSearchChange}
          style={{ borderRadius: "24px", margin: "1rem", background: cardtitlesdivs, width: "calc(12rem + 12vw)" }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "20px" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none", // Removes the border from the outline
              },
            },
          }}
        />

        {loading ? (
          <p>{t('vendingMachines.Loading')}</p>
        ) : (
          <>
            <TransactionsTable
              rows={rows || []}
              columns={columns || []}
              selectedRows={selectedRows || []}
              selectAll={selectAll}
            />
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                display: 'flex',
                justifyContent: 'center', // Center the pagination controls
                alignItems: 'center',
              }}
            />
          </>
        )}
      </Box>

      {/* Modal Popup */}
      <TaxModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />

    </div>
  )
}

export default Taxes